import styled, { css } from 'styled-components';
import { defaultTheme } from '@styles/theme.styled';
import { generateScales } from '@helpers/generate-media-queries';
import Image from '@components/image';
import LinkComponent from '@components/link';
import Grid from '@components/grid';

interface LinkComponentProps {
  fontColor: keyof typeof defaultTheme.colors;
}

export const StyledLink = styled(LinkComponent)<LinkComponentProps>`
  ${({ theme }) => {
    const { mobile, desktop } = theme.fontSize.body;
    return css`
      margin: 0;
      ${generateScales('font-size', mobile, desktop, theme)}
      font-weight: 500;
      width: fit-content;
    `;
  }}
`;

interface TextContainerProps {
  $alignment?: boolean;
}

export const TextContainer = styled.div<TextContainerProps>`
  ${({ theme, $alignment }) => css`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: ${$alignment ? 'flex-start' : 'center'};
    text-align: ${$alignment ? 'left' : 'center'};
    row-gap: 1rem;
    width: 100%;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      justify-content: flex-start;
      align-items: flex-start;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      width: 100%;
      text-align: left;
    }
  `}
`;

export const StyledGrid = styled(Grid)`
  ${({ theme }) => css`
    gap: 1.25rem;

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      row-gap: 1rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      grid-template-columns: repeat(2, 1fr);
      row-gap: 2rem;
      justify-content: flex-start;
      column-gap: 2rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      grid-template-columns: repeat(1, 1fr);
      row-gap: 1.5rem;
      justify-content: flex-start;
      column-gap: 2rem;
    }
  `}
`;

export const ImageContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    height: 5rem;
    margin: 0 auto;
    padding: 1.5rem;
    max-width: 5rem;
    background-color: ${theme.colors.secondary};
    border-radius: 100%;
    width: 100%;
    margin-bottom: 1.5rem;

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      height: 4rem;
      max-width: 4rem;
      padding: 1.125rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      margin-bottom: 0;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      height: 3rem;
      max-width: 3rem;
      padding: 0.75rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      margin-bottom: 0;
    }
  `}
`;

interface ContainerProps {
  isLink: boolean;
  $enclosedContainer?: boolean;
}

export const Container = styled.div<ContainerProps>`
  ${({ theme, isLink, $enclosedContainer }) => css`
    display: flex;
    flex-direction: column;
    background-color: ${$enclosedContainer ? `${theme.backgroundColors.light}66` : 'transparent'};
    border-radius: ${theme.borderRadius.normal};
    padding: ${$enclosedContainer ? '2.5rem 1.75rem' : '0'};
    transition: transform 0.125s ease-in-out;

    ${isLink &&
    css`
      cursor: pointer;
      &:hover {
        transform: scale(1.0375);

        ${StyledLink} a {
          background-size: 100% 0.15rem;
        }
      }
    `};

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      text-align: left;
      width: 100%;
      flex-direction: row;
      column-gap: 1.5rem;
      align-items: center;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      padding: ${$enclosedContainer ? '2rem 1.5rem' : '0'};
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      border-radius: 1rem;
      flex-direction: row;
      padding: ${$enclosedContainer ? '1.75rem 1rem' : '0'};
      text-align: left;
      align-items: center;
      column-gap: 1rem;
    }
  `}
`;

export const StyledImage = styled(Image)`
  width: 100%;
  height: 100%;
`;
